import React from "react";
//#region types
import {
  FileViewerContainerProps,
  FileViewerContainerState,
  FileMetaData,
  ImageMeta,
  FileViewerContainerInnerProps,
} from "models/propTypes/fileViewerPropTypes";
import { DialogEnums, FileEnums } from "models/enums";
import { FileConstants } from "utils/constants";
import { FileHelper } from "utils/helpers";
//#endregion
//#region components
import DialogEvents from "../dialogs/dialogEvents";
import FileViewer from "./fileViewer";
import DialogContainer from "../dialogs/dialogContainer";
import { Grid } from "@mui/material";
import { useImageService } from "serviceHooks";
//#endregion
import cssExports from "theme/styles/main.module.scss";

const FileViewerContainer: React.FC<FileViewerContainerProps> = (props) => {
  const imageService = useImageService();

  return <FileViewerContainerInner {...props} getImage={imageService.getImage}></FileViewerContainerInner>;
};

class FileViewerContainerInner extends React.Component<FileViewerContainerInnerProps, FileViewerContainerState> {
  mounted: boolean = false;

  constructor(props: FileViewerContainerInnerProps) {
    super(props);

    const initialIndex = props.initialIndex ?? 0;

    this.state = {
      images: [],
      currentPage: initialIndex + 1,
      currentIndex: initialIndex,
      currentFile: undefined,
      isLoading: props.meta && props.meta?.length > 0 ? true : false,
    };
  }

  componentDidMount() {
    this.mounted = true;
    this.initializeState();
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  componentDidUpdate(prevProps: FileViewerContainerProps) {
    if (JSON.stringify(prevProps.meta) !== JSON.stringify(this.props.meta)) this.initializeState();
  }

  initializeState = () => {
    this.setState(
      (prevState) => {
        const newMetaInfo = this.getImagesMetaInfo(this.props.meta);

        return {
          images: newMetaInfo,
          currentPage: prevState.currentPage,
          currentIndex: prevState.currentIndex,
          currentFile: newMetaInfo[prevState.currentIndex],
          isLoading:
            this.props.meta && this.props.meta.length > 0
              ? this.props.meta[0].loading ?? true
              : this.props.loading
                ? true
                : false,
        };
      },
      () => this.loadImage(this.state.currentIndex)
    );
  };

  getImagesMetaInfo = (meta?: ImageMeta[]): FileMetaData[] => {
    const items = meta?.length
      ? meta
        /* eslint-disable-next-line array-callback-return -- the return in the else logic is insid the loop. **/
        .map((meta, index) => {
          if (meta.numberOfPages === 1)
            return [
              {
                imageId: meta.id,
                contentType: meta.contentType,
                pageNumber: 0,
                data: meta.data ?? "",
                imageType: meta.imageType,
                index: index,
                fileName: meta.fileName,
                locked: meta.locked,
              },
            ];
          else {
            const internalImages: FileMetaData[] = [];
            for (let i = 0; i < meta.numberOfPages; i++) {
              internalImages.push({
                imageId: meta.id,
                contentType: meta.contentType ?? "",
                pageNumber: i,
                data: meta.data ?? "",
                imageType: meta.imageType,
                index: index,
                fileName: meta.fileName,
                locked: meta.locked,
              });

              if (i === meta.numberOfPages - 1) return internalImages;
            }
          }
        })
        .flat(1)
      : [];

    return items as unknown as FileMetaData[];
  };

  loadImage = (index: number) => {
    if (this.state.images?.length && !this.props.meta![index]?.loading) {
      this.props.onCurrentIndexChange?.(index);
      this.setState(
        {
          ...this.state,
          isLoading: true,
          currentFile: undefined,
          currentIndex: index,
          currentPage: index + 1,
        },
        () => {
          const currImage = this.state.images[index];
          if (!currImage) return;
          currImage.index = index;

          const setImage = (contentType: string, imageResult: string) => {
            this.mounted &&
              this.setState((prevState) => {
                const images = [...prevState.images];
                images[index].data = imageResult;
                images[index].index = index;

                // Normalize contentType using fileName, existing contentType, and file data
                images[index].contentType = FileHelper.normalizeContentType(
                  currImage.fileName,
                  contentType,
                  imageResult
                );

                if (index !== prevState.currentIndex) {
                  return {
                    ...prevState,
                    images,
                  };
                }
                return {
                  ...prevState,
                  images: images,
                  currentFile: images[index],
                  currentPage: index + 1,
                  isLoading: false,
                };
              });
          };
          if (currImage.data) {
            this.mounted &&
              this.setState({
                ...this.state,
                currentFile: currImage,
                currentPage: index + 1,
                isLoading: false,
              });
          } else if (this.props.type !== undefined) {
            this.props
              .getImage(currImage.imageId, currImage.imageType ?? this.props.type, currImage.pageNumber)
              .then(
                setImage.bind(
                  null,
                  currImage.contentType?.toLowerCase() === FileEnums.ContentTypes.ApplicationPDF
                    ? FileEnums.ContentTypes.ImagePNG
                    : currImage.contentType
                )
              )
              .catch(() =>
                setImage(
                  currImage.contentType?.toLowerCase() === FileEnums.ContentTypes.ApplicationPDF
                    ? FileEnums.ContentTypes.ImagePNG
                    : currImage.contentType,
                  ""
                )
              );
          } else if (this.props.getFile !== undefined) {
            this.props
              .getFile(currImage.imageId, index)
              .then((file) =>
                typeof file === "string"
                  ? setImage(currImage.contentType, file)
                  : setImage(currImage.contentType || file.contentType || "", file.data ?? "")
              )
              .catch(() => setImage(currImage.contentType, ""));
          }
        }
      );
    }
  };

  updateMetaInfo = (index: number, id: number | string, replaceFrom: FileMetaData[]) => {
    this.setState((prevState) => {
      const updatedImages = [...prevState.images];
      const updatedImagesReversed = [...prevState.images].reverse();

      const startIndex = updatedImages.findIndex((image) => image.imageId === id);
      const lastIndex = prevState.images.length - updatedImagesReversed.findIndex((image) => image.imageId === id);

      const images = [...updatedImages.slice(0, startIndex), ...replaceFrom, ...updatedImages.slice(lastIndex)];
      return { ...prevState, images };
    });
  };

  onPreview = (index: number, func: () => void) => {
    this.loadImage(index);
    func();
  };

  render() {
    return (
      <DialogEvents
        render={(isOpen, openDialog, closeDialog, key) => (
          <>
            {this.props.children ? (
              React.cloneElement(this.props.children, {
                openPreview: (index: number) => this.onPreview(index, openDialog),
              })
            ) : (
              <div
                style={{
                  width: "100%",
                  visibility: isOpen ? "hidden" : "visible",
                }}
              >
                <FileViewer
                  key="fileViewer"
                  {...this.props}
                  {...this.state}
                  loadImage={this.loadImage}
                  updateMetaInfo={this.updateMetaInfo}
                  imageCount={this.state.images.length}
                  onClick={openDialog}
                />
              </div>
            )}

            <DialogContainer
              isOpen={isOpen}
              title={<></>}
              dialogType={DialogEnums.DialogType.ImageZoom}
              closeDialog={closeDialog}
              className={cssExports.transparent}
            >
              <Grid
                id={FileConstants.FileViewerFullScreenId}
                container
                direction="row"
                justifyContent="center"
                alignItems="center"
                className={cssExports.fileViewerFullScreen}
                onClick={closeDialog}
              >
                <FileViewer
                  key="fileViewer-fullScreen"
                  {...this.props}
                  {...this.state}
                  loadImage={this.loadImage}
                  updateMetaInfo={this.updateMetaInfo}
                  imageCount={this.state.images.length}
                  fullScreen
                />
              </Grid>
            </DialogContainer>
          </>
        )}
      />
    );
  }
}

export default FileViewerContainer;
